/**
 * Client-side rate limiting using localStorage.
 * Tracks timestamps of actions and enforces cooldowns.
 */

const RATE_LIMIT_PREFIX = "weborder.ratelimit.";

/**
 * Check if an action is rate-limited.
 * @param {string} action - e.g. "login", "register", "checkout", "upload"
 * @param {number} maxAttempts - Maximum allowed attempts in the window
 * @param {number} windowMs - Time window in milliseconds
 * @returns {{ allowed: boolean, remainingMs: number, attempts: number }}
 */
export function checkRateLimit(action, maxAttempts = 5, windowMs = 60000) {
  const key = RATE_LIMIT_PREFIX + action;
  const now = Date.now();

  let attempts = [];
  try {
    attempts = JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    attempts = [];
  }

  // Remove expired entries outside the window
  attempts = attempts.filter((ts) => now - ts < windowMs);

  if (attempts.length >= maxAttempts) {
    const oldestInWindow = attempts[0];
    const remainingMs = oldestInWindow + windowMs - now;
    return { allowed: false, remainingMs: Math.max(0, remainingMs), attempts: attempts.length };
  }

  return { allowed: true, remainingMs: 0, attempts: attempts.length };
}

/**
 * Record an action attempt for rate limiting.
 * @param {string} action - e.g. "login", "register", "checkout", "upload"
 */
export function recordAction(action) {
  const key = RATE_LIMIT_PREFIX + action;
  const now = Date.now();

  let attempts = [];
  try {
    attempts = JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    attempts = [];
  }

  attempts.push(now);

  // Keep only last 50 entries to prevent localStorage bloat
  if (attempts.length > 50) {
    attempts = attempts.slice(-50);
  }

  localStorage.setItem(key, JSON.stringify(attempts));
}

/**
 * Clear rate limit for a specific action (e.g. after successful action).
 * @param {string} action
 */
export function clearRateLimit(action) {
  localStorage.removeItem(RATE_LIMIT_PREFIX + action);
}

/**
 * Get human-readable remaining time string.
 * @param {number} ms
 * @returns {string}
 */
export function formatRemainingTime(ms) {
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds} စက္ကန့်`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} မိနစ်`;
}

// ─── Pre-configured rate limits for each action ───

export function checkLoginRateLimit() {
  return checkRateLimit("login", 5, 120000); // 5 attempts per 2 minutes
}

export function checkRegisterRateLimit() {
  return checkRateLimit("register", 3, 600000); // 3 attempts per 10 minutes
}

export function checkCheckoutRateLimit() {
  return checkRateLimit("checkout", 8, 300000); // 8 attempts per 5 minutes
}

export function checkUploadRateLimit() {
  return checkRateLimit("upload", 10, 300000); // 10 uploads per 5 minutes
}

export function recordLoginAttempt() { recordAction("login"); }
export function recordRegisterAttempt() { recordAction("register"); }
export function recordCheckoutAttempt() { recordAction("checkout"); }
export function recordUploadAttempt() { recordAction("upload"); }
export function clearLoginRateLimit() { clearRateLimit("login"); }
export function clearRegisterRateLimit() { clearRateLimit("register"); }
export function clearCheckoutRateLimit() { clearRateLimit("checkout"); }
export function clearUploadRateLimit() { clearRateLimit("upload"); }
